import React, { useState, useEffect, useRef } from "react";
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSpinner, faArrowLeft,
  faComment, faStar
} from '@fortawesome/free-solid-svg-icons';
import { Card, Button, Table, Alert, Modal, Form, Badge } from '@themesberg/react-bootstrap';
import api from '../../utils/api';

export default () => {
  const history = useHistory();
  const [quickMessages, setQuickMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Form states
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isSmart, setIsSmart] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Drag and drop refs
  const dragItem = useRef();
  const dragOverItem = useRef();

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    // Adding class to indicate dragging could be done via e.currentTarget.classList
  };

  const handleDragEnter = (e, index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = async () => {
    if (dragItem.current === undefined || dragOverItem.current === undefined) return;
    if (dragItem.current === dragOverItem.current) return;

    try {
      const _quickMessages = [...quickMessages];
      const draggedItemContent = _quickMessages.splice(dragItem.current, 1)[0];
      _quickMessages.splice(dragOverItem.current, 0, draggedItemContent);

      dragItem.current = null;
      dragOverItem.current = null;

      // Local state'i hemen güncelle (snappy UI)
      setQuickMessages(_quickMessages);

      // Firebase'e batch ile kaydet yerine API çağrısı
      const updateOrderPromises = _quickMessages.map((msg, index) => {
        const newOrder = index + 1;
        msg.order = newOrder;
        return api.put(`/admin-messages/quick/${msg.id}`, { order: newOrder });
      });

      await Promise.all(updateOrderPromises);

    } catch (err) {
      console.error("Error saving new order:", err);
      setError("Sıralama kaydedilirken hata oluştu.");
      loadQuickMessages();
    }
  };

  useEffect(() => {
    loadQuickMessages();
  }, []);

  const loadQuickMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/admin-messages/quick');
      const messages = response.data?.messages || [];

      // Sıralama - küçükten büyüğe
      messages.sort((a, b) => {
        const orderA = a.order !== undefined ? Number(a.order) : 999;
        const orderB = b.order !== undefined ? Number(b.order) : 999;
        return orderA - orderB;
      });

      setQuickMessages(messages);
    } catch (err) {
      console.error('Error loading quick messages:', err);
      setError('Hızlı mesajlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setTitle("");
    setContent("");
    setIsSmart(false);
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setTitle("");
    setContent("");
    setIsSmart(false);
  };

  const handleOpenEditModal = (message) => {
    setSelectedMessage(message);
    setTitle(message.title);
    setContent(message.content);
    setIsSmart(message.isSmart || false);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedMessage(null);
    setTitle("");
    setContent("");
    setIsSmart(false);
  };

  const handleSaveMessage = async () => {
    if (!title.trim() || !content.trim()) {
      setError('Başlık ve mesaj içeriği gereklidir.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await api.post('/admin-messages/quick', {
        title: title.trim(),
        content: content.trim(),
        isSmart: isSmart,
        order: quickMessages.length + 1
      });

      await loadQuickMessages();
      handleCloseAddModal();
    } catch (err) {
      console.error('Error saving quick message:', err);
      setError('Hızlı mesaj kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMessage = async () => {
    if (!title.trim() || !content.trim()) {
      setError('Başlık ve mesaj içeriği gereklidir.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await api.put(`/admin-messages/quick/${selectedMessage.id}`, {
        title: title.trim(),
        content: content.trim(),
        isSmart: isSmart
      });

      await loadQuickMessages();
      handleCloseEditModal();
    } catch (err) {
      console.error('Error updating quick message:', err);
      setError('Hızlı mesaj güncellenirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteConfirm = (message) => {
    setSelectedMessage(message);
    setShowDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setSelectedMessage(null);
  };

  const handleDeleteMessage = async () => {
    if (!selectedMessage) return;

    try {
      setDeleting(true);
      setError(null);

      await api.delete(`/admin-messages/quick/${selectedMessage.id}`);

      await loadQuickMessages();
      handleCloseDeleteConfirm();
    } catch (err) {
      console.error('Error deleting quick message:', err);
      setError('Hızlı mesaj silinirken bir hata oluştu.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <Button variant="secondary" onClick={() => history.goBack()} className="me-3">
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <h4 className="mb-0">Hızlı Mesajlar</h4>
          </div>
          <div className="d-flex gap-2">
            {/* Güncelleme/Düzeltme Butonu */}
            <Button variant="warning" onClick={() => history.push('/messages/reply/select')}>
              <FontAwesomeIcon icon={faComment} className="me-2" />
              Mesaj Gönder
            </Button>
            {/* Artı Butonu - Yeni Mesaj Ekle */}
            <Button variant="success" onClick={handleOpenAddModal}>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Yeni Mesaj
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faSpinner} className="fa-spin fa-2x" />
              <p className="mt-3">Yükleniyor...</p>
            </div>
          ) : quickMessages.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Henüz hızlı mesaj yok.</p>
              <Button variant="success" onClick={handleOpenAddModal}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                İlk Mesajı Ekle
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '50px' }}>#</th>
                    <th style={{ width: '80px' }}>Sıra</th>
                    <th style={{ width: '200px' }}>Başlık</th>
                    <th>Mesaj İçeriği</th>
                    <th style={{ width: '150px' }}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {quickMessages.map((msg, index) => (
                    <tr
                      key={msg.id}
                      style={{
                        ...(msg.isSmart ? { backgroundColor: 'rgba(255, 193, 7, 0.05)' } : {}),
                        cursor: 'grab'
                      }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnter={(e) => handleDragEnter(e, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <td className="text-center">{index + 1}</td>
                      <td className="text-center">
                        <Badge bg={msg.order !== undefined && msg.order !== 999 ? "info" : "secondary"}>
                          {msg.order !== undefined && msg.order !== 999 ? msg.order : '-'}
                        </Badge>
                      </td>
                      <td>
                        <strong>{msg.title}</strong>
                        {msg.isSmart && (
                          <FontAwesomeIcon
                            icon={faStar}
                            className="ms-2 text-warning"
                            title="Akıllı Mesaj"
                          />
                        )}
                      </td>
                      <td>
                        <div style={{
                          maxWidth: '400px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {msg.content}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => handleOpenEditModal(msg)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleOpenDeleteConfirm(msg)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {!loading && quickMessages.length > 0 && (
            <div className="mt-3">
              <span className="text-muted">Toplam {quickMessages.length} hızlı mesaj</span>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Yeni Mesaj Ekleme Modal */}
      <Modal show={showAddModal} onHide={handleCloseAddModal}>
        <Modal.Header closeButton>
          <Modal.Title>Yeni Hızlı Mesaj</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Başlık</Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Örn: Selamlaşma"
              autoFocus
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Mesaj İçeriği</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Merhaba canım nasılsın?"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="isSmartAdd"
              label="Akıllı Mesaj (Değişkenleri otomatik doldurur)"
              checked={isSmart}
              onChange={(e) => setIsSmart(e.target.checked)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAddModal} disabled={saving}>
            İptal
          </Button>
          <Button variant="success" onClick={handleSaveMessage} disabled={saving}>
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Kaydet
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mesaj Düzenleme Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal}>
        <Modal.Header closeButton>
          <Modal.Title>Hızlı Mesaj Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Başlık</Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Örn: Selamlaşma"
              autoFocus
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Mesaj İçeriği</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Merhaba canım nasılsın?"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="isSmartEdit"
              label="Akıllı Mesaj (Değişkenleri otomatik doldurur)"
              checked={isSmart}
              onChange={(e) => setIsSmart(e.target.checked)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal} disabled={saving}>
            İptal
          </Button>
          <Button variant="warning" onClick={handleUpdateMessage} disabled={saving}>
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                Güncelleniyor...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Güncelle
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Silme Onayı Modal */}
      <Modal show={showDeleteConfirm} onHide={handleCloseDeleteConfirm}>
        <Modal.Header closeButton>
          <Modal.Title>Mesaj Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu hızlı mesajı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteConfirm} disabled={deleting}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDeleteMessage} disabled={deleting}>
            {deleting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                Siliniyor...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Sil
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
